"use client";

import "./rich-text-editor.css";

import * as React from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ClickableLinkPlugin } from "@lexical/react/LexicalClickableLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { HeadingNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";

import { getLexicalTheme } from "./kernel/composer/getLexicalTheme";
import { LEXICAL_NAMESPACE } from "./kernel/constants";
import { TailEmptyParagraphPlugin } from "./kernel/plugins/TailEmptyParagraphPlugin";
import { EmptyParagraphPlaceholderPlugin } from "./kernel/plugins/EmptyParagraphPlaceholderPlugin";
import { InitialFocusPlugin } from "./kernel/plugins/InitialFocusPlugin";
import { ClipboardAsyncReadPastePlugin } from "./kernel/plugins/ClipboardAsyncReadPastePlugin";
import { LexicalClipboardPasteFocusPlugin } from "./kernel/plugins/LexicalClipboardPasteFocusPlugin";
import { NodeSelectionClipboardPlugin } from "./kernel/plugins/NodeSelectionClipboardPlugin";
import { RejectBinaryImagePastePlugin } from "./kernel/plugins/RejectBinaryImagePastePlugin";
import { BlockHandleOverlayPlugin } from "./interaction/drag-handle/BlockHandleOverlayPlugin";
import { SlashMenuPlugin } from "./interaction/menus/SlashMenuPlugin";
import { TextToolbarPlugin } from "./interaction/toolbars/TextToolbarPlugin";
import { KeyboardBlockNavigationPlugin } from "./interaction/keyboard/KeyboardBlockNavigationPlugin";
import { KeyboardListIndentPlugin } from "./interaction/keyboard/KeyboardListIndentPlugin";
import { ImageBlockNode } from "./kernel/nodes/ImageBlockNode";
import { TableInteractionPlugin } from "./interaction/floating/table";

export type LexicalSerializedState = unknown;

export type RichTextEditorProps = {
  mode?: "edit" | "readonly";
  /** 受控初始/外部文档；不传或 `null` 为空白文档。组件内不做 localStorage。 */
  value?: LexicalSerializedState | null;
  /** 内容变化时把序列化状态交给使用方；是否本地存储/接口保存由使用方实现。 */
  onChange?: (value: LexicalSerializedState) => void;
  /** 正文失焦（如录入段落结束点击别处），用于触发外部立即保存等 */
  onBlur?: () => void;
  className?: string;
  /**
   * 默认 `false`：根节点 `h-full` + 自身 `overflow-y-auto`，在固定高度容器内滚动。
   * 为 `true` 时高度随正文增高，由外层滚动容器（如文档页中栏）统一纵向滚动。
   */
  growWithContent?: boolean;
};

/**
 * Lexical 富文本编辑器：无内置持久化，仅通过 `value` / `onChange` 与外层同步。
 */
export function RichTextEditor({
  mode = "edit",
  value = null,
  onChange,
  onBlur,
  className,
  growWithContent = false,
}: RichTextEditorProps) {
  const isEditable = mode === "edit";
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const initialConfig = React.useMemo(() => {
    return {
      namespace: LEXICAL_NAMESPACE,
      editable: isEditable,
      theme: getLexicalTheme(),
      nodes: [HeadingNode, ListNode, ListItemNode, LinkNode, TableNode, TableRowNode, TableCellNode, ImageBlockNode],
      onError: (error: Error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
      editorState:
        value != null
          ? (editor: unknown) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const e = editor as any;
              const nextState = e.parseEditorState(value);
              e.setEditorState(nextState);
            }
          : undefined,
    };
  }, [isEditable, value]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full p-12 overscroll-x-contain ${
        growWithContent ? "min-h-0 h-auto overflow-visible" : "h-full overflow-y-auto"
      } ${className ?? ""}`}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          ErrorBoundary={LexicalErrorBoundary}
          contentEditable={
            <ContentEditable
              className="rt-content-editable flex flex-col gap-4 outline-none min-h-[24px]"
              onBlur={onBlur}
            />
          }
          placeholder={null}
        />

        <HistoryPlugin />
        <LinkPlugin validateUrl={(url) => /^https?:\/\//i.test(url)} />
        <ClickableLinkPlugin newTab disabled={false} />
        <ListPlugin />
        <TablePlugin hasCellMerge hasCellBackgroundColor hasHorizontalScroll />
        <LexicalClipboardPasteFocusPlugin />
        <NodeSelectionClipboardPlugin />
        <ClipboardAsyncReadPastePlugin />
        <RejectBinaryImagePastePlugin />

        <TailEmptyParagraphPlugin isEditable={isEditable} />
        <InitialFocusPlugin isEditable={isEditable} />
        <EmptyParagraphPlaceholderPlugin isEditable={isEditable} containerRef={containerRef} />

        <BlockHandleOverlayPlugin isEditable={isEditable} containerRef={containerRef} />
        <SlashMenuPlugin isEditable={isEditable} containerRef={containerRef} />
        <TextToolbarPlugin isEditable={isEditable} containerRef={containerRef} />
        <TableInteractionPlugin isEditable={isEditable} containerRef={containerRef} />
        <KeyboardBlockNavigationPlugin isEditable={isEditable} />
        <KeyboardListIndentPlugin isEditable={isEditable} />

        <OnChangePlugin
          onChange={(editorState) => {
            onChange?.(editorState.toJSON() as LexicalSerializedState);
          }}
        />
      </LexicalComposer>
    </div>
  );
}

